// Constants
import Constants from './Constants';

// Delete property from object and return new object
export const omit = (object, deletedKey) => {
  const newObj = {};
  for (const key in object) {
    if (Object.hasOwnProperty.call(object, key)) {
      if (key === deletedKey) {
        continue;
      }
      newObj[key] = object[key];
    }
  }
  return newObj;
};

// get user name from email
export const getUsernameFromEmail = (email) => {
  return email.substring(0, email.indexOf('@'));
};

// Compute book reading progress
export const getReadingProgress = (currentPage, totalPages) => {
  if (totalPages === 0) {
    return 0;
  }

  return Math.round((currentPage / totalPages) * 100);
};

/**
 *  write and delete user data to local storage
 */

export const writeUserToLocalStorage = (user) => {
  localStorage.setItem(Constants.USER_KEY, JSON.stringify(user));
};

export const removeUserFromLocalStorage = () => {
  localStorage.removeItem(Constants.USER_KEY);
};

export const getUser = () => {
  const user = localStorage.getItem(Constants.USER_KEY);
  return user && JSON.parse(user);
};

/**
 *  write and delete best seller books to local storage
 */

export const writeBestSellerToLocalStorage = (response) => {
  localStorage.setItem(Constants.BEST_SELLER_KEY, JSON.stringify(response));
};

export const removeBestSellerFromLocalStorage = () => {
  localStorage.removeItem(Constants.BEST_SELLER_KEY);
};

export const getBestSellerFromLocalStorage = () => {
  const response = localStorage.getItem(Constants.BEST_SELLER_KEY);

  if (!response) {
    return null;
  }
  const parsedResponse = JSON.parse(response);

  const nextUpdateDate = Date.parse(parsedResponse?.next_update);

  if (Date.now > nextUpdateDate) {
    removeBestSellerFromLocalStorage();
    return null;
  }

  return parsedResponse;
};

export const markBestSellerBooksAsFavourite = () => {
  const bestSellerState = getBestSellerFromLocalStorage();

  if (!bestSellerState) {
    return;
  }

  const userBooksState = getUserBooksFromSessionStorage();

  if (!userBooksState) {
    return;
  }

  const userBooks = userBooksState.books_list;

  const updateList = (list) => {
    list.forEach((book, index) => {
      for (let i = 0; i < userBooks.length; i++) {
        const element = userBooks[i];
        if (element._id === book._id) {
          list[index] = {
            ...book,
            listName: element.listName,
            currentPage: element.currentPage || 0,
          };
        }
      }
    });

    return list;
  };

  bestSellerState.books_list = updateList(bestSellerState.books_list);

  writeBestSellerToLocalStorage(bestSellerState);
};

export const markBestSellerBook = (bookId, listName) => {
  const bestSellerState = getBestSellerFromLocalStorage();
  const userBooksState = getUserBooksFromSessionStorage();

  if (!userBooksState) {
    return;
  }

  const userBooks = userBooksState.books_list;

  for (let i = 0; i < userBooks.length; i++) {
    const userBook = userBooks[i];

    if (userBook._id === bookId) {
      for (let j = 0; j < bestSellerState.books_list.length; j++) {
        const currentBook = bestSellerState.books_list[j];

        if (currentBook._id === bookId) {
          bestSellerState.books_list[j] = {
            ...currentBook,
            listName,
            currentPage: userBook.currentPage || 0,
          };
          break;
        }
        break;
      }
    }
  }

  writeBestSellerToLocalStorage(bestSellerState);
};

export const unMarkBestSellerBook = (bookId) => {
  const bestSellerState = getBestSellerFromLocalStorage();

  for (let i = 0; i < bestSellerState.books_list.length; i++) {
    const book = bestSellerState.books_list[i];

    if (book._id === bookId) {
      bestSellerState.books_list[i] = {
        ...book,
        listName: '',
        currentPage: 0,
      };
      break;
    }
  }

  writeBestSellerToLocalStorage(bestSellerState);
};

export const isBookFavourite = (bookId) => {
  let book = null;
  const userBooksState = getUserBooksFromSessionStorage();

  if (!userBooksState) {
    return null;
  }

  const userBooks = userBooksState.books_list;

  for (let i = 0; i < userBooks.length; i++) {
    const userBook = userBooks[i];

    if (userBook._id === bookId) {
      book = userBook;
    }
  }
  return book;
};

export const updateBestSellerBookCurrentPage = (bookId, page) => {
  const bestSellerState = getBestSellerFromLocalStorage();

  for (let i = 0; i < bestSellerState.books_list.length; i++) {
    const book = bestSellerState.books_list[i];

    if (book._id === bookId) {
      bestSellerState.books_list[i] = {
        ...book,
        currentPage: page,
      };
      break;
    }
  }

  writeBestSellerToLocalStorage(bestSellerState);
};

/**
 *  write and delete user books to session storage
 */

export const writeUserBooksToSessionStorage = (response) => {
  sessionStorage.setItem(Constants.USER_BOOKS_KEY, JSON.stringify(response));
};

export const removeUserBooksFromSessionStorage = () => {
  sessionStorage.removeItem(Constants.USER_BOOKS_KEY);
};

export const getUserBooksFromSessionStorage = () => {
  const response = sessionStorage.getItem(Constants.USER_BOOKS_KEY);

  return response && JSON.parse(response);
};

export const addBookToUserBooks = (book) => {
  let userBooks = getUserBooksFromSessionStorage();
  if (!userBooks) {
    userBooks = {};
  }
  userBooks.books_list.push(book);
  userBooks.total_results = userBooks.books_list.length;
  writeUserBooksToSessionStorage(userBooks);
};

export const removeBookFromUserBooks = (bookId) => {
  const userBooks = getUserBooksFromSessionStorage();
  const filteredList = userBooks.books_list.filter(
    (book) => book._id !== bookId
  );
  userBooks.books_list = filteredList;
  userBooks.total_results = userBooks.books_list.length;
  writeUserBooksToSessionStorage(userBooks);
};

export const updateUserBookCurrentPage = (bookId, page) => {
  const userBooks = getUserBooksFromSessionStorage();

  for (let i = 0; i < userBooks.books_list.length; i++) {
    const book = userBooks.books_list[i];

    if (book._id === bookId) {
      book.currentPage = page;
      userBooks.books_list[i] = book;
      break;
    }
  }
  writeUserBooksToSessionStorage(userBooks);
};

/**
 *  write and get search results to session storage
 */

export const writeSearchDataToSessionStorage = (
  title = '',
  author = '',
  data
) => {
  // key is string in format "title_author"
  const key = convertSearchParamsToKey(title, author);
  sessionStorage.setItem(key, JSON.stringify(data));
  // write search params as last search
  sessionStorage.setItem(
    Constants.LAST_SEARCH_PARAMS,
    JSON.stringify({ ...data, searchParams: { title, author } })
  );
};

export const getLastSearchDataFromSessionStorage = () => {
  const data = sessionStorage.getItem(Constants.LAST_SEARCH_PARAMS);

  return data && JSON.parse(data);
};

export const getSearchDataFromSessionStorage = (title = '', author = '') => {
  const key = convertSearchParamsToKey(title, author);
  const data = sessionStorage.getItem(key);

  if (!data) {
    return null;
  }

  const searchParams = convertKeyToSearchParams(key);

  const parsedData = JSON.parse(data);
  parsedData.searchParams = searchParams;

  return parsedData;
};

const convertSearchParamsToKey = (title, author) => {
  return `${title}_${author}`;
};

const convertKeyToSearchParams = (key) => {
  const splitArray = key.split('_');

  const title = splitArray[0];
  const author = splitArray[1];

  return { title, author };
};

// clear storage for logout & delete account
export const clearStorage = () => {
  localStorage.clear();
  sessionStorage.clear();
};
